import * as React from 'react'
import { cn } from '@/lib/utils'

export interface RadioProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'type' | 'checked' | 'defaultChecked' | 'onChange'> {
  checked: boolean
  /** Called when this one is chosen; a radio is never un-chosen by itself. */
  onSelect: () => void
}

/**
 * Plan View's radio (`.ds-radio`), the checkbox's single-choice sibling: a
 * 16px circle on the strong line that fills with `primary` around a small dot
 * when chosen. It keeps its round corner, because round is what says "one of
 * these".
 *
 * A native input underneath, so arrow keys move through radios that share a
 * `name` and a screen reader announces the group. Put it in a `<label>` with its
 * words, or give it an `aria-label` where a column header names it.
 */
export function Radio({ checked, onSelect, className, ...props }: RadioProps) {
  return (
    <span className={cn('relative inline-flex h-4 w-4 shrink-0', className)}>
      <input
        type="radio"
        checked={checked}
        onChange={(event) => {
          if (event.target.checked) onSelect()
        }}
        className="peer m-0 h-4 w-4 appearance-none rounded-round border border-line-strong bg-surface transition-colors duration-100 ease-out checked:border-primary checked:bg-primary focus-visible:focus-ds disabled:cursor-not-allowed disabled:opacity-50 motion-reduce:transition-none"
        {...props}
      />
      <span
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 m-auto h-1.5 w-1.5 scale-0 rounded-round bg-surface transition-transform duration-100 ease-out peer-checked:scale-100 motion-reduce:transition-none"
      />
    </span>
  )
}
